// Property freshness — small pure helpers used by the badge / banner UI.
//
// The organisation entity carries `freshnessFreshDays` and
// `freshnessStaleDays` (e.g. fresh = 7, stale = 14). Buckets:
//   - fresh:   age <= freshDays
//   - warning: freshDays < age <= staleDays
//   - stale:   age > staleDays  OR  lastFreshnessConfirmedAt is null
// "stale" is what the dashboard and list badge surface as "Cần xác nhận".

export type FreshnessLevel = "fresh" | "warning" | "stale" | "unknown";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole days between {@code from} (now by default) and the timestamp. */
export function daysSince(
  iso: string | null,
  now: Date = new Date(),
): number | null {
  if (iso === null) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = now.getTime() - then;
  return Math.floor(diff / MS_PER_DAY);
}

/**
 * Classify a property's freshness using the org's thresholds. A null
 * `lastFreshnessConfirmedAt` is treated as stale — never confirmed.
 */
export function freshnessLevel(
  lastFreshnessConfirmedAt: string | null,
  freshDays: number,
  staleDays: number,
  now: Date = new Date(),
): FreshnessLevel {
  if (lastFreshnessConfirmedAt === null) return "unknown";
  const age = daysSince(lastFreshnessConfirmedAt, now);
  if (age === null) return "unknown";
  if (age <= freshDays) return "fresh";
  if (age <= staleDays) return "warning";
  return "stale";
}

/** True when the property should display the "Cần xác nhận" amber badge. */
export function isStale(
  lastFreshnessConfirmedAt: string | null,
  staleDays: number,
  now: Date = new Date(),
): boolean {
  if (lastFreshnessConfirmedAt === null) return true;
  const age = daysSince(lastFreshnessConfirmedAt, now);
  if (age === null) return true;
  return age > staleDays;
}
